<template>
  <div class="profile-container">
    <h3>account profile - {{currentAdvertiser.name}} </h3>
    <div class="error-message">{{pageError}}</div>
    <loading-icon v-if="isLoading"></loading-icon>

    <div v-else-if="advertiser" class="field-container">

      <profile-input
          :currentAdvertiserId="currentAdvertiser.id"
          label="business timezone"
          type="select"
          id="time_zone"
          :value="advertiser.standard_time_zone"
          :timezones="timezones">
      </profile-input>

      <div class="field-wrapper">
        <label>state(s) in which you operate<sup>*</sup></label>

        <sb-us-states-dropdown
          :selected.sync="selectedStates"
          @submit="submitLocations"
        />

        <div :class="['response', { show: response.locations.message, error: response.locations.hasError }]" style="width: 318px">{{ response.locations.message }}</div>
      </div>

      <profile-input
          :currentAdvertiserId="currentAdvertiser.id"
          label="contact name"
          type="text"
          id="contact_name"
          :value="advertiser.contact_name">
      </profile-input>

      <profile-input
          :currentAdvertiserId="currentAdvertiser.id"
          label="contact phone"
          type="tel"
          id="contact_phone"
          :value="contactPhone">
      </profile-input>

      <profile-input
          :currentAdvertiserId="currentAdvertiser.id"
          label="business phone"
          type="tel"
          id="business_phone"
          :value="businessPhone">
      </profile-input>

      <profile-input
          :currentAdvertiserId="currentAdvertiser.id"
          label="business email"
          type="email"
          id="email"
          :value="advertiser.email">
      </profile-input>

      <profile-input
          :currentAdvertiserId="currentAdvertiser.id"
          label="business address"
          type="text"
          id="address"
          :value="advertiser.address">
      </profile-input>

      <profile-input
          :currentAdvertiserId="currentAdvertiser.id"
          label="business city"
          type="text"
          id="city"
          :value="advertiser.city">
      </profile-input>

      <profile-input
          :currentAdvertiserId="currentAdvertiser.id"
          label="business state"
          type="text"
          id="state"
          :value="advertiser.state">
      </profile-input>

      <profile-input
          :currentAdvertiserId="currentAdvertiser.id"
          label="business zip"
          type="number"
          id="postal"
          :value="advertiser.postal">
      </profile-input>
    </div>
  </div>
</template>

<script>
  import ProfileInput from '../Profile/ProfileInput.vue'
  import LoadingIcon from '../Shared/LoadingIcon'
  import {USStates} from './us-states'
  import {brandsAPI} from "../../services/api/brands"

  export default {
    props: {
      currentAdvertiser: Object,
    },

    name: 'Profile',

    components: {
      ProfileInput,
      LoadingIcon
    },

    data() {
      return {
        advertiser: null,
        fields: null,
        isLoading: true,
        pageError: null,
        timezones: [],
        selectedStates: [],
        response: {
          locations: { isError: false, message: '' }
        }
      }
    },

    methods: {
      getTimeZones() {
        const url = `/brands/timezones`
        this.isLoading = true
        this.pageError = null
        this.$api.get(url).then((response) => {
          const data = response.data
          if (data) {
            this.timezones = data
          } else {
            this.pageError = 'there was an error updating ad, please refresh or contact support'
          }
          this.isLoading = false
        }).catch((error) => {
          this.pageError = `there was an issue updating ad: ${error}`
          this.isLoading = false
        })
      },

      getProfile() {
        const url = `/brands/${this.currentAdvertiser.id}`

        this.isLoading = true
        this.pageError = null

        this.$api.get(url).then((response) => {
          const data = response.data
          if (data) {
            this.advertiser = data
            this.getTimeZones()

            this.setSelectedStates(data.states_with_license || []);

            this.isLoading = false
          } else {
            this.pageError = 'there was an error updating ad, please refresh or contact support'
            this.isLoading = false
          }
        }).catch((error) => {
          this.pageError = `there was an issue updating ad: ${error}`
          this.isLoading = false
        })
      },
      /**
       * Standardizes state names and remove duplicates
       *
       * @param {string[]} selectedStates
       */
      setSelectedStates(selectedStates) {
        const statesSet = new Set();

        selectedStates.forEach((selectedState) => {
          const correctState = USStates.find((state) => state.toLowerCase() === selectedState.toLowerCase());

          if(correctState) {
            statesSet.add(correctState)
          }
        });

        this.selectedStates = Array.from(statesSet.values());
      },

      formatPhone(phone) {
        const phoneTest = new RegExp(/^((\+1)|1)? ?\(?(\d{3})\)?[ .-]?(\d{3})[ .-]?(\d{4})( ?(ext\.? ?|x)(\d*))?$/)

        phone = phone?.trim()
        const results = phoneTest.exec(phone)
        if (results !== null && results.length > 8) {
          return "(" + results[3] + ") " + results[4] + "-" + results[5] + (typeof results[8] !== "undefined" ? " x" + results[8] : "")
        }
        else {
          return phone
        }
      },

      submitLocations(states) {
        if(states.length === 0) {
          this.response.locations = { isError: true, message: 'Please select a state'}
          return;
        }

        brandsAPI.updateAdvertiser(this.currentAdvertiser.id, {
          'states_with_license': states,
        }).then(() => {
          this.response.locations = { isError: false, message: 'changes saved' };
        }).catch((err) => {
          this.response.locations = { isError: true, message: err };
        });
      }
    },

    created() {
      this.getProfile()
    },

    computed: {
      businessPhone: function() {
        return this.formatPhone(this.advertiser?.business_phone?.toString())
      },

      contactPhone: function() {
        return this.formatPhone(this.advertiser?.contact_phone?.toString())
      },

      statesWithLicense: function() {
        return this.advertiser?.states_with_license || []
      },
    },
  }
</script>

<style lang="sass">
@import "../../assets/sass/colors"

.profile-container
  padding-top: 2rem
  text-align: center

  h3
    margin-bottom: 2rem

  .field-container
    align-items: center
    display: flex
    flex-direction: column
    justify-content: center
    text-align: left

  .states-instructions
    p
      font-size: .9rem
      margin: .15rem auto

    span
      font-weight: bold

  .sb-multi-dropdown
    width: 318px
    label
      margin-top: 4px
    .query-container
      border-bottom: 0
    .label-container
      background-color: white
      min-height: unset
      padding: .5rem
      .selected
        font-size: 1rem
        text-align: left
    .item
      .sb-checkbox
        border-width: 2px
        height: 10px
        width: 10px

  .sb-multi-dropdown.not-empty
    .label-container
      align-items: flex-start
      color: black
      .icon-container
        color: $primary-color
</style>